import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Sparkles } from "lucide-react";
import axios from "axios";
import { ProductCard } from "../components/ProductCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
	const [products, setProducts] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchProducts = async () => {
			try {
				// Seed database first
				await axios.post(`${API}/seed`);

				const response = await axios.get(`${API}/products`);
				setProducts(response.data);
			} catch (err) {
				console.error("Error fetching products:", err);
			} finally {
				setLoading(false);
			}
		};

		fetchProducts();
	}, []);

	const collections = [
		{
			name: "ASTRO",
			description: "Esplora l'universo con stile",
			image:
				"https://images.unsplash.com/photo-1614732414444-096e5f1122d5?w=800",
			color: "bg-accent-blue",
			collection: "astro",
		},
		{
			name: "DRINKS",
			description: "Brinda alla vita",
			image:
				"https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=800",
			color: "bg-accent-pink",
			collection: "drinks",
		},
		{
			name: "QUOTES",
			description: "Parole che ispirano",
			image:
				"https://images.unsplash.com/photo-1489533119213-66a5cd877091?w=800",
			color: "bg-accent-yellow",
			collection: "quotes",
		},
	];

	const renderMarqueeItems = (keyPrefix) =>
		[...Array(6)].map((_, i) => (
			<span
				key={`${keyPrefix}-${i}`}
				className="inline-flex items-center mx-8 gap-4"
			>
				<Star className="text-accent-yellow" size={20} fill="currentColor" />
				<span className="font-heading text-2xl text-white leading-none">
					RIKAMI
				</span>
				<Star className="text-accent-pink" size={20} fill="currentColor" />
				<span className="font-body text-lg text-gray-400 leading-none">
					URBAN STYLE
				</span>
			</span>
		));

	const homemadeProducts = products.filter((product) => product.category === "homemade");
	const otherProducts = products.filter((product) => product.category !== "homemade");
	const featuredProducts = [...homemadeProducts, ...otherProducts].slice(0, 8);

	return (
		<div data-testid="home-page">
			{/* Hero Section */}
			<section className="relative h-screen bg-white overflow-hidden flex flex-col">
				<div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full border-[12px] border-black/10"></div>
				<div className="pointer-events-none absolute top-20 -right-16 h-56 w-56 rounded-full border-[10px] border-accent-pink/20"></div>
				<div className="pointer-events-none absolute bottom-24 left-1/2 h-40 w-40 rounded-full border-[8px] border-accent-blue/20"></div>
				<div className="flex-1 flex items-center">
					<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 w-full">
						<div className="grid lg:grid-cols-2 gap-12 items-center">
						{/* Left Content */}
						<motion.div
							initial={{ opacity: 0, x: -50 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.6 }}
						>
							<div className="inline-flex items-center px-4 py-2 border-2 border-black bg-accent-yellow mb-6">
								<Sparkles size={20} className="mr-2" />
								<span className="font-mono text-sm uppercase">
									Nuove Collezioni
								</span>
							</div>

							<h1 className="font-heading text-6xl sm:text-7xl lg:text-8xl leading-none mb-6">
								WEAR THE
								<br />
								<span className="text-accent-pink">NOISE</span>
							</h1>

							<p className="font-body text-lg sm:text-xl text-gray-700 mb-8 max-w-lg">
								Abbigliamento ricamato per chi osa essere diverso. Ogni pezzo è
								unico, come te. Fatto a mano in Italia.
							</p>

							<div className="flex flex-wrap gap-4">
								<Link
									to="/magliette"
									className="btn-brutal inline-flex items-center"
									data-testid="shop-magliette-btn"
								>
									SHOP MAGLIETTE
									<ArrowRight size={20} className="ml-2" />
								</Link>
								<Link
									to="/felpe"
									className="btn-brutal-outline inline-flex items-center"
									data-testid="shop-felpe-btn"
								>
									SHOP FELPE
									<ArrowRight size={20} className="ml-2" />
								</Link>
							</div>

							<div className="mt-8 grid grid-cols-3 gap-4">
								<div className="border-2 border-black p-3 bg-white shadow-brutal">
									<p className="font-heading text-2xl text-black">100%</p>
									<p className="font-mono text-xs text-gray-600">RICAMO A MANO</p>
								</div>
								<div className="border-2 border-black p-3 bg-accent-yellow shadow-brutal">
									<p className="font-heading text-2xl text-black">48H</p>
									<p className="font-mono text-xs text-black/70">DROP VELOCI</p>
								</div>
								<div className="border-2 border-black p-3 bg-black shadow-brutal">
									<p className="font-heading text-2xl text-white">IT</p>
									<p className="font-mono text-xs text-white/70">MADE IN ITALY</p>
								</div>
							</div>
						</motion.div>

						{/* Right Image Grid */}
						<motion.div
							initial={{ opacity: 0, x: 50 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.6, delay: 0.2 }}
							className="relative"
						>
							<div className="grid grid-cols-2 gap-4">
								<div className="space-y-4">
									<div className="border-4 border-black shadow-brutal-lg overflow-hidden">
										<img
											src="https://images.unsplash.com/photo-1576566588028-4147f3842f27?w=600"
											alt="RIKAMI Style"
											className="w-full h-64 object-cover"
										/>
									</div>
									<div className="border-4 border-black bg-accent-pink p-6">
										<p className="font-heading text-2xl text-white">100%</p>
										<p className="font-mono text-sm text-white">
											RICAMATO A MANO
										</p>
									</div>
								</div>
								<div className="space-y-4 pt-8">
									<div className="border-4 border-black bg-black p-6">
										<p className="font-heading text-4xl text-accent-yellow">
											3
										</p>
										<p className="font-mono text-sm text-white">COLLEZIONI</p>
									</div>
									<div className="border-4 border-black shadow-brutal-lg overflow-hidden">
										<img
											src="https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=600"
											alt="RIKAMI Hoodie"
											className="w-full h-72 object-cover"
										/>
									</div>
								</div>
							</div>
						</motion.div>
					</div>
					</div>
				</div>

				{/* Marquee */}
				<div className="bg-black py-4 border-y-4 border-accent-pink marquee">
					<div className="marquee-track">
						<div className="marquee-group">{renderMarqueeItems("a")}</div>
						<div className="marquee-group" aria-hidden="true">
							{renderMarqueeItems("b")}
						</div>
					</div>
				</div>
			</section>

			{/* Collections Section */}
			<section className="py-20 bg-white" data-testid="collections-section">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
						<div>
							<h2 className="font-heading text-5xl sm:text-6xl mb-4">
								LE COLLEZIONI
							</h2>
							<p className="font-body text-lg text-gray-600 max-w-xl">
								Tre universi, uno stile. Scegli la tua vibe.
							</p>
						</div>
					</div>

					<div className="grid md:grid-cols-3 gap-6">
						{collections.map((collection, index) => (
							<motion.div
								key={collection.name}
								initial={{ opacity: 0, y: 30 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ delay: index * 0.1 }}
								viewport={{ once: true }}
								data-testid={`collection-${collection.collection}`}
							>
								<Link
									to={`/magliette?collection=${collection.collection}`}
									className="block group"
								>
									<div className="card-brutal overflow-hidden">
										<div className="aspect-[4/5] overflow-hidden border-b-2 border-black">
											<img
												src={collection.image}
												alt={collection.name}
												className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
											/>
										</div>
										<div
											className={`${collection.color} p-6 ${collection.color === "bg-accent-pink" ? "text-white" : "text-black"}`}
										>
											<h3 className="font-heading text-3xl mb-2">
												{collection.name}
											</h3>
											<p className="font-body">{collection.description}</p>
											<div className="mt-4 flex items-center font-mono text-sm uppercase">
												ESPLORA
												<ArrowRight
													size={16}
													className="ml-2 group-hover:translate-x-2 transition-transform"
												/>
											</div>
										</div>
									</div>
								</Link>
							</motion.div>
						))}
					</div>
				</div>
			</section>

			{/* Featured Products */}
			<section
				className="py-20 bg-gray-50 border-y-4 border-black"
				data-testid="featured-section"
			>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
						<div>
							<h2 className="font-heading text-5xl sm:text-6xl mb-4">
								ULTIMI ARRIVI
							</h2>
							<p className="font-body text-lg text-gray-600">
								I pezzi più freschi, appena usciti.
							</p>
						</div>
						<Link
							to="/abbigliamento"
							className="btn-brutal-outline mt-6 md:mt-0 inline-flex items-center w-fit"
						>
							VEDI TUTTO
							<ArrowRight size={20} className="ml-2" />
						</Link>
					</div>

					{loading ? (
						<div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
							{[...Array(4)].map((_, i) => (
								<div
									key={i}
									className="border-2 border-black p-4 animate-pulse"
								>
									<div className="aspect-square bg-gray-200 mb-4"></div>
									<div className="h-6 bg-gray-200 mb-2"></div>
									<div className="h-4 bg-gray-200 w-2/3"></div>
								</div>
							))}
						</div>
					) : (
						<div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
							{featuredProducts.map((product) => (
								<ProductCard key={product.product_id} product={product} />
							))}
						</div>
					)}
				</div>
			</section>

			{/* Manifesto Section */}
			<section className="py-20 bg-black text-white">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="grid lg:grid-cols-2 gap-12 items-center">
						<div>
							<h2 className="font-heading text-5xl sm:text-6xl lg:text-7xl mb-8">
								NON SEGUIRE
								<br />
								<span className="text-accent-pink">LE REGOLE</span>
							</h2>
							<p className="font-body text-xl text-gray-300 mb-8">
								RIKAMI nasce per chi non si accontenta. Per chi vuole indossare
								qualcosa di unico. Per chi sa che lo stile non si compra, si
								crea.
							</p>
							<div className="grid grid-cols-3 gap-6">
								<div className="border-2 border-accent-yellow p-4">
									<p className="font-heading text-3xl text-accent-yellow">
										100%
									</p>
									<p className="font-mono text-xs text-gray-400">
										COTONE PREMIUM
									</p>
								</div>
								<div className="border-2 border-accent-blue p-4">
									<p className="font-heading text-3xl text-accent-blue">∞</p>
									<p className="font-mono text-xs text-gray-400">STILE UNICO</p>
								</div>
								<div className="border-2 border-accent-pink p-4">
									<p className="font-heading text-3xl text-accent-pink">IT</p>
									<p className="font-mono text-xs text-gray-400">
										MADE IN ITALY
									</p>
								</div>
							</div>
						</div>
						<div className="relative">
							<div className="border-4 border-accent-pink shadow-brutal-pink overflow-hidden">
								<img
									src="https://images.unsplash.com/photo-1529374255404-311a2a4f1fd9?w=800"
									alt="RIKAMI Style"
									className="w-full h-96 object-cover"
								/>
							</div>
							<div className="absolute -bottom-6 -right-6 bg-accent-yellow border-4 border-black p-6 text-black">
								<p className="font-heading text-2xl">RICAMO</p>
								<p className="font-mono text-sm text-black/80">FATTO A MANO</p>
							</div>
						</div>
					</div>
				</div>
			</section>
		</div>
	);
}
